<template>
    <div class="dispatch-parameters">
        <div class="content-block dx-card responsive-paddings">
            <div class="params-header">
                <h5>Parameters</h5>
                <DxButton text="Clear" icon="clear" styling-mode="text" @click="clearAll" />
            </div>
            <!-- <p class="muted">Define what this dispatch is for. Operator and Area are optional.</p> -->

            <div class="params-grid">
                <!-- Name spans both columns. -->
                <div class="field span-both">
                    <h6>Dispatch Name <span class="req">*</span> :</h6>
                    <DxTextBox v-model:value="form.name" placeholder="e.g. North Field Methanol Run" />
                </div>

                <!-- Column 1: the run itself. -->
                <div class="params-col">
                    <div class="field">
                        <h6>Dispatch Date <span class="req">*</span> :</h6>
                        <DxDateBox v-model:value="form.dispatchDate" type="date"
                            placeholder="Select a date" :show-clear-button="true" />
                    </div>

                    <div class="field">
                        <h6>Delivery Method <span class="req">*</span> :
                            <span id="hintDeliveryMethod" class="hint-icon dx-icon-info"
                                role="img" aria-label="About delivery methods"></span>
                        </h6>
                        <DxSelectBox :data-source="deliveryMethods" value-expr="value" display-expr="text"
                            v-model:value="form.deliveryMethod" />
                        <DxTooltip target="#hintDeliveryMethod" show-event="mouseenter"
                            hide-event="mouseleave" position="top" :max-width="320"
                            :wrapper-attr="{ class: 'param-hint-tooltip' }">
                            <!-- Both methods are described, whichever is selected, so the
                                 tooltip explains the choice rather than the current value. -->
                            <div class="hint-tip">
                                <p class="hint-tip-item">
                                    <strong>Tanker (bulk)</strong> &mdash; product is pumped from a
                                    single-shell tanker, so a truck carries one product per run. The
                                    Supply step asks how much bulk you have.
                                </p>
                                <p class="hint-tip-item">
                                    <strong>Totes (deck truck)</strong> &mdash; product travels in IBC
                                    totes. The Supply step asks for each product's tote size and how
                                    many you have.
                                </p>
                            </div>
                        </DxTooltip>
                    </div>

                    <div class="field">
                        <h6>Maximum DTE <span class="opt">(days to empty)</span> :</h6>
                        <DxNumberBox v-model:value="form.maxDte" :min="0" :show-spin-buttons="true"
                            placeholder="Maximum days to empty" />
                    </div>
                </div>

                <!-- Column 2: which sites the run covers. -->
                <div class="params-col">
                    <div class="field">
                        <h6>Product <span class="req">*</span> <span class="opt">(multi-select)</span> :
                            <span id="hintProduct" class="hint-icon dx-icon-info"
                                role="img" aria-label="About selecting products"></span>
                        </h6>
                        <DxTagBox :data-source="products" v-model:value="form.product"
                            placeholder="Select one or more products" :search-enabled="true"
                            :show-selection-controls="true" :show-clear-button="true"
                            :max-displayed-tags="3" :show-multi-tag-only="true" />
                        <DxTooltip target="#hintProduct" show-event="mouseenter"
                            hide-event="mouseleave" position="top" :max-width="320"
                            :wrapper-attr="{ class: 'param-hint-tooltip' }">
                            <div class="hint-tip">
                                A truck can only carry one product per compartment.
                                <template v-if="form.product.length > 1">
                                    Delivery planning will split these {{ form.product.length }}
                                    products across compartments, which usually needs more trucks or
                                    more trips than a single-product run.
                                </template>
                                <template v-else>
                                    Picking several products makes delivery planning split them across
                                    compartments, which usually needs more trucks or more trips than a
                                    single-product run.
                                </template>
                            </div>
                        </DxTooltip>
                    </div>

                    <div class="field">
                        <h6>Operator <span class="opt">(optional, multi-select)</span> :</h6>
                        <DxTagBox :data-source="operators" value-expr="operatorName"
                            display-expr="operatorName" v-model:value="form.operator"
                            placeholder="Select one or more operators" :search-enabled="true"
                            :show-selection-controls="true" :show-clear-button="true"
                            :max-displayed-tags="3" :show-multi-tag-only="true"
                            @value-changed="onOperatorChanged" />
                    </div>

                    <div class="field">
                        <h6>Area <span class="opt">(optional, multi-select)</span>
                            <span v-if="form.operator.length" class="opt">&mdash; limited to areas
                                {{ form.operator.length === 1 ? 'this operator works' : 'these operators work' }}</span>
                        </h6>
                        <DxTagBox :data-source="areas" value-expr="areaName" display-expr="areaName"
                            v-model:value="form.area" placeholder="Select one or more areas"
                            :search-enabled="true" :show-selection-controls="true"
                            :show-clear-button="true" :max-displayed-tags="3"
                            :show-multi-tag-only="true" />
                    </div>
                </div>
            </div>

            <div v-if="error" class="error-msg">{{ error }}</div>
        </div>

        <div class="nav-row">
            <DxButton icon="chevronleft" text="Previous" styling-mode="outlined" @click="onPrevious" />
            <DxButton icon="chevronright" text="Next" type="default" styling-mode="contained"
                @click="onNext" />
        </div>
    </div>
</template>

<script>
import DxSelectBox from 'devextreme-vue/select-box';
import DxTagBox from 'devextreme-vue/tag-box';
import DxTextBox from 'devextreme-vue/text-box';
import DxNumberBox from 'devextreme-vue/number-box';
import DxDateBox from 'devextreme-vue/date-box';
import { DxButton } from 'devextreme-vue/button';
import DxTooltip from 'devextreme-vue/tooltip';

export default {
    name: 'DispatchParameters',
    components: {
        DxSelectBox,
        DxTagBox,
        DxTextBox,
        DxNumberBox,
        DxDateBox,
        DxButton,
        DxTooltip,
    },
    emits: ['previous', 'next'],
    data() {
        return {
            error: null,
            deliveryMethods: [
                { value: 'tanker', text: 'Tanker (bulk)' },
                { value: 'totes', text: 'Totes (deck truck)' },
            ],
            // Set once the form has been seeded from the store, so hydrating a saved
            // dispatch doesn't look like the user changing the operator.
            hydrated: false,
            form: {
                name: '',
                dispatchDate: null,
                deliveryMethod: 'tanker',
                product: [],
                operator: [],
                area: [],
                maxDte: null,
            },
        };
    },
    computed: {
        products() {
            return this.$store.getters['dispatch/getProducts'];
        },
        operators() {
            return this.$store.getters['operators/getOperators'];
        },
        // Which areas each operator works. Loaded by the builder.
        operatorAreas() {
            return this.$store.getters['operators/getOperatorsAreas'] || [];
        },
        // Every area, or — once operators are chosen — only the ones those
        // operators actually work, so the picker can't offer a pairing that would
        // match no sites.
        areas() {
            const all = this.$store.getters['areas/getAreas'] || [];
            if (!this.form.operator.length) return all;

            const allowed = new Set(
                this.operatorAreas
                    .filter((x) => this.form.operator.includes(x.operatorName))
                    .map((x) => x.areaName)
            );
            // Keep anything already selected in the list as well: a dispatch saved
            // before the mapping changed should still show the area it stored
            // rather than silently losing it from the box.
            this.form.area.forEach((name) => allowed.add(name));

            return all.filter((a) => allowed.has(a.areaName));
        },
        currentDispatch() {
            return this.$store.getters['dispatch/getCurrentDispatch'];
        },
    },
    created() {
        // Hydrate the form from the current dispatch (new or loaded). Operator
        // and Area are multi-select arrays; loaded/recent dispatches may store
        // them as a single string, so normalize to an array.
        const cd = this.currentDispatch;
        this.form.name = cd.name || '';
        this.form.dispatchDate = cd.parameters.dispatchDate ?? null;
        this.form.deliveryMethod = cd.parameters.deliveryMethod === 'totes' ? 'totes' : 'tanker';
        // Older dispatches stored a single product string, and an "all products"
        // dispatch stored no product at all; both normalise to the array form.
        this.form.product = this.toArray(cd.parameters.product);
        this.form.operator = this.toArray(cd.parameters.operator);
        this.form.area = this.toArray(cd.parameters.area);
        this.form.maxDte = cd.parameters.maxDte ?? null;
        this.$nextTick(() => {
            this.hydrated = true;
        });
    },
    methods: {
        // Areas belong to operators, so any area picked under a previous operator
        // selection is no longer necessarily valid — clear it and let the user
        // choose again from the narrowed list.
        onOperatorChanged() {
            if (!this.hydrated) return;
            this.form.area = [];
        },
        toArray(value) {
            if (Array.isArray(value)) return value;
            return value ? [value] : [];
        },
        clearAll() {
            this.form.name = '';
            this.form.dispatchDate = null;
            this.form.deliveryMethod = 'tanker';
            this.form.product = [];
            this.form.operator = [];
            this.form.area = [];
            this.form.maxDte = null;
            this.error = null;
        },
        persist() {
            // Write name (dispatch root) and parameters in one commit.
            const cd = this.currentDispatch;
            this.$store.commit('dispatch/setCurrentDispatch', {
                ...cd,
                name: this.form.name,
                parameters: {
                    dispatchDate: this.form.dispatchDate,
                    deliveryMethod: this.form.deliveryMethod,
                    product: this.form.product,
                    operator: this.form.operator,
                    area: this.form.area,
                    maxDte: this.form.maxDte,
                },
            });
        },
        onPrevious() {
            this.persist();
            this.$emit('previous');
        },
        onNext() {
            if (!this.form.name || !this.form.name.trim()) {
                this.error = 'Dispatch Name is required.';
                return;
            }
            if (!this.form.product.length) {
                this.error = 'Select at least one product.';
                return;
            }
            this.error = null;
            this.persist();
            this.$emit('next');
        },
    },
};
</script>

<style scoped>
.dispatch-parameters {
    /* Wider than the old single column, so two columns of editors still have
       comfortable field widths. */
    max-width: 980px;
    margin: 0 auto;
}

.params-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    /* Column gap only -- the fields supply their own vertical rhythm. */
    gap: 0 36px;
    align-items: start;
}

/* Dispatch Name sits above both columns. */
.span-both {
    grid-column: 1 / -1;
}

/* minmax(0, 1fr) above lets these shrink; without it a wide editor would push
   the column past its share. */
.params-col {
    min-width: 0;
}

/* Two columns stop helping once each would be too narrow for a tag box. */
@media (max-width: 800px) {
    .params-grid {
        grid-template-columns: minmax(0, 1fr);
    }
}

.params-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

h5 {
    margin-top: 0;
    margin-bottom: 0;
    font-family: Roboto, Verdana, Geneva, Tahoma, sans-serif;
    font-weight: 300;
}

.field {
    margin-bottom: 6px;
}

h6 {
    margin: 22px 0 6px 0;
    font-size: 14px;
    font-weight: lighter;
}

.req {
    color: rgb(255, 115, 0);
}

/* The information symbol beside a parameter label; hovering it opens the
   tooltip that replaced the old inline hint paragraph. */
.hint-icon {
    margin-left: 6px;
    font-size: 14px;
    color: rgba(150, 150, 150, 0.9);
    cursor: help;
    vertical-align: middle;
}

.hint-icon:hover {
    color: rgb(255, 140, 40);
}

.hint-tip {
    font-size: 12px;
    line-height: 1.5;
    text-align: left;
}

/* Two descriptions stacked in one tooltip; no outer margins so the widget's
   own padding sets the edges. */
.hint-tip-item {
    margin: 0;
}

.hint-tip-item + .hint-tip-item {
    margin-top: 8px;
}

.hint-tip-item strong {
    font-weight: 500;
    color: rgb(255, 160, 70);
}

.opt {
    color: rgba(150, 150, 150, 0.9);
    font-size: 12px;
}

.muted {
    color: rgba(150, 150, 150, 0.9);
    font-size: 13px;
}

.error-msg {
    margin-top: 16px;
    color: #d9534f;
    font-size: 13px;
}

.nav-row {
    display: flex;
    justify-content: space-between;
    margin: 20px;
}
</style>

<!-- Not scoped on purpose: a DxTooltip is appended to document.body, outside
     this component's DOM, so neither the scoped data attribute nor :deep()
     reaches the widget's own wrapper classes. wrapperAttr tags these two
     tooltips with .param-hint-tooltip so nothing else in the app is touched. -->
<style>
/* The theme styles tooltips for one short centred line:
   .dx-tooltip-wrapper .dx-overlay-content sets line-height: 0 and
   .dx-popup-content sets white-space: nowrap + display: inline-block. That is
   why multi-sentence content ran onto a single line with the background
   covering only part of it. Selectors here carry one extra class than the
   theme's so they win regardless of stylesheet order. */
.dx-tooltip-wrapper.param-hint-tooltip .dx-overlay-content {
    line-height: normal;
    text-align: left;
}

/* The theme adds a full-height ::before to vertically centre single-line
   inline-block content; with wrapped block content it just adds a stray line. */
.dx-tooltip-wrapper.param-hint-tooltip .dx-overlay-content::before {
    display: none;
}

.dx-tooltip-wrapper.param-hint-tooltip .dx-overlay-content .dx-popup-content {
    display: block;
    white-space: normal;
    text-align: left;
    padding: 10px 12px;
}
</style>
